import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Teachr from "../../Objects/models/Teachr";
import PunctualCourseRepository from "../../Repositories/PunctualCourseRepository";
import { GET_NEXT_COURSE } from "../../Redux/Actions/PunctualCourseActions";
import { DraftGroup } from "./types";
import { draftGroupsFromApi, findDraft } from "./drafts";

/** Élèves par page : au-delà, la liste est paginée (même pagination serveur que l'onglet existant). */
export const DRAFTS_PAGE_SIZE = 10;

/**
 * Brouillons du prof : même appel que l'onglet existant
 * (`getCourseNextMonth`, cours du mois précédent), rangé dans la même clé
 * Redux `punctualCourse.nextCourse` pour que l'onglet « Brouillons » et le
 * tableau de bord restent cohérents. `reload` après une déclaration ou une
 * suppression.
 */
const useDrafts = (teachr: Teachr | null | undefined) => {
    const dispatch = useDispatch();
    const stored = useSelector((s: any) => s?.punctualCourse?.nextCourse);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [page, setPage] = useState(0);

    const load = useCallback(async (pageToLoad = 0) => {
        if (!teachr?.id) return;
        setLoading(true);
        setFailed(false);
        try {
            const payload = await PunctualCourseRepository.getCourseNextMonth(teachr.id, pageToLoad, DRAFTS_PAGE_SIZE);
            dispatch({ type: GET_NEXT_COURSE, payload });
            setPage(pageToLoad);
        } catch (e) {
            console.warn("Chargement des brouillons échoué", e);
            setFailed(true);
        } finally {
            setLoading(false);
        }
    }, [teachr?.id, dispatch]);

    useEffect(() => {
        load(0);
    }, [load]);

    const groups: DraftGroup[] = useMemo(() => draftGroupsFromApi(stored, teachr), [stored, teachr]);
    /** Nombre d'élèves (groupes) toutes pages confondues, renvoyé par l'API. */
    const total: number = stored?.totalItems ?? groups.length;
    const pageCount = Math.max(1, Math.ceil(total / DRAFTS_PAGE_SIZE));

    return { groups, total, pageCount, loading, failed, page, reload: () => load(page), loadPage: load };
};

/** Un brouillon par identifiant : dans le store, sinon après rechargement de la liste. */
export const useDraft = (teachr: Teachr | null | undefined, id: number | null) => {
    const { groups, loading, failed, reload, page, pageCount, loadPage } = useDrafts(teachr);
    const draft = useMemo(() => (id ? findDraft(groups, id) : null), [groups, id]);
    const [searched, setSearched] = useState(false);
    // Arrivée directe sur l'URL : le brouillon peut être sur une page suivante de la liste.
    useEffect(() => {
        if (loading || failed || draft || !id) return;
        if (page < pageCount - 1) {
            loadPage(page + 1);
        } else {
            setSearched(true);
        }
    }, [loading, failed, draft, id, page, pageCount, loadPage]);
    return { draft, loading: loading || (!draft && !failed && !searched), failed, reload, found: !!draft };
};

export default useDrafts;
